import { useFormikContext } from "formik";
import { Alert, Button, Card, Col } from "react-bootstrap";
import { object } from "yup";
import AccordionCustom, {
  useAccordionState,
} from "../../../components/Accordion/Accordion.tsx";
import {
  FormContainer,
  TextAreaInput,
  TextInput,
} from "../../../components/Form/Form.tsx";
import MyFormik from "../../../components/Form/MyFormik.tsx";
import SelectForm from "../../../components/Form/SelectForm.tsx";
import { IconInfo } from "../../../components/Icon/Icon.tsx";
import url, { getFetchOptions } from "../../../module/fetch.tsx";
import { IdCodeLibelleType } from "../../../utils/typeUtils.tsx";
import { formatData, QueryParam } from "../Constants.tsx";

type QueryFormProps = {
  validateAbortRef?: React.MutableRefObject<AbortController | null>;
  activeQuery: QueryParam;
  setActiveQuery: (query: QueryParam) => void;
  setQueryData: (data: Record<string, unknown>[] | null) => void;
  setAvailableOptions: (options: string[]) => void;
  zoneCompetenceList: IdCodeLibelleType[];
  utilisateurList: IdCodeLibelleType[];
  organismeList: IdCodeLibelleType[];
  sqlErrorMessage: string | null;
  setSqlErrorMessage: (message: string | null) => void;
};

const QueryForm = (props: QueryFormProps) => {
  const urlApiQuery = url`/api/dashboard/validate-query`;

  const handleTestQuery = async (values: {
    query: string;
    queryTitle: string;
  }) => {
    props.validateAbortRef?.current?.abort();

    const controller = new AbortController();
    if (props.validateAbortRef) {
      props.validateAbortRef.current = controller;
    }

    try {
      const response = await fetch(urlApiQuery, {
        ...getFetchOptions({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: values.query,
            queryTitle: values.queryTitle,
          }),
        }),
        signal: controller.signal,
      });

      const raw = await response.text();
      let parsed: unknown = raw;

      try {
        parsed = raw ? JSON.parse(raw) : null;
      } catch {
        // réponse non JSON => erreur SQL brute
      }

      if (typeof parsed === "string") {
        props.setSqlErrorMessage(parsed);
        return;
      }

      if (!response.ok) {
        const message =
          parsed && typeof parsed === "object" && "message" in parsed
            ? String((parsed as { message?: unknown }).message ?? "Erreur SQL")
            : "Erreur SQL";
        props.setSqlErrorMessage(message);
        return;
      }

      props.setSqlErrorMessage(null);
      updateData(
        parsed as {
          queryTitle: string;
          querySql: string;
          name: string[];
          values: unknown[];
        },
      );
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      props.setSqlErrorMessage(
        error instanceof Error ? error.message : String(error),
      );
    }
  };

  const updateData = (data: {
    queryTitle: string;
    querySql: string;
    name: string[];
    values: unknown[];
  }) => {
    props.setActiveQuery({
      ...props.activeQuery,
      title: data.queryTitle,
      query: data.querySql,
    });
    const dataFormatted = formatData(data);
    props.setQueryData(dataFormatted);
    props.setAvailableOptions(
      dataFormatted?.[0] ? Object.keys(dataFormatted[0]) : [],
    );
    props.setSqlErrorMessage(null);
  };

  const getInitialValues = (activeQuery: QueryParam) => ({
    queryId: activeQuery.id,
    query: activeQuery.query,
    queryTitle: activeQuery.title,
  });

  return (
    <Card bg="secondary" className="m-3">
      <Card.Body>
        <MyFormik
          initialValues={getInitialValues(props.activeQuery)}
          isPost={true}
          submitUrl={urlApiQuery}
          onSubmit={(values) => updateData(values)}
          successToastMessage="La requête est valide"
          prepareVariables={(values) => values}
          validationSchema={object({})}
        >
          <InnerQueryForm
            zoneCompetenceList={props.zoneCompetenceList}
            utilisateurList={props.utilisateurList}
            organismeList={props.organismeList}
            sqlErrorMessage={props.sqlErrorMessage}
            onTest={handleTestQuery}
          />
        </MyFormik>
      </Card.Body>
    </Card>
  );
};

const InnerQueryForm = ({
  zoneCompetenceList,
  utilisateurList,
  organismeList,
  sqlErrorMessage,
  onTest,
}: {
  zoneCompetenceList: IdCodeLibelleType[];
  utilisateurList: IdCodeLibelleType[];
  organismeList: IdCodeLibelleType[];
  sqlErrorMessage: string | null;
  onTest: (values: { query: string; queryTitle: string }) => void;
}) => {
  const { values, setValues } = useFormikContext<{
    queryId: string | number;
    query: string;
    queryTitle: string;
    zoneCompetenceId?: string | number;
    utilisateurId?: string | number;
    organismeId?: string | number;
  }>();

  const { activesKeys, handleShowClose } = useAccordionState(
    Array(2).fill(false),
  );

  return (
    <FormContainer>
      {sqlErrorMessage && (
        <span className="text-danger">{sqlErrorMessage}</span>
      )}

      <AccordionCustom
        activesKeys={activesKeys}
        list={[
          {
            header: "Informations générales",
            content: (
              <span>
                <IconInfo /> Certaines requêtes ont besoin de s&apos;appuyer sur
                des paramètres qui dépendent de l&apos;utilisateur connecté :
                <ul>
                  <li>l&apos;organisme</li>
                  <li>la zone de compétence</li>
                  <li>l&apos;utilisateur en lui-même</li>
                </ul>
                Ces paramètres sont accessibles au travers de 3 constantes qui
                seront remplacées à l&apos;exécution :
                <ul>
                  <li>#ORGANISME_ID#</li>
                  <li>#ZONE_COMPETENCE_ID#</li>
                  <li>#UTILISATEUR_ID#</li>
                </ul>
                A utiliser tel quel, et ne pas entourer de quotes ou autre.
                Exemple pour une jointure sur les données de l&apos;organisme de
                l&apos;utilisateur connecté : &quot;WHERE organisme_id =
                #ORGANISME_ID#&quot;
              </span>
            ),
          },
          {
            header: "Paramètres",
            content: (
              <>
                <span>
                  Lorsque les constantes sont utilisées, il faut fournir une
                  valeur &quot;cohérente&quot; dans la requête, pour que
                  celle-ci retourne des résultats qui permettront de construire
                  les visualisations. Les valeurs saisies ne sont ni stockées ni
                  utilisées par l&apos;utilisateur final du tableau de bord,
                  pour lequel elles sont remplacées par la &quot;bonne&quot;
                  valeur
                </span>
                <SelectForm
                  name={"zoneCompetenceId"}
                  listIdCodeLibelle={zoneCompetenceList}
                  label="Zone de compétence"
                  required={false}
                  setValues={setValues}
                  defaultValue={zoneCompetenceList?.find(
                    (e: IdCodeLibelleType) => e.id === values.zoneCompetenceId,
                  )}
                />
                <SelectForm
                  name={"utilisateurId"}
                  listIdCodeLibelle={utilisateurList}
                  label="Utilisateur"
                  required={false}
                  setValues={setValues}
                  defaultValue={utilisateurList?.find(
                    (e: IdCodeLibelleType) => e.id === values.utilisateurId,
                  )}
                />
                <SelectForm
                  name={"organismeId"}
                  listIdCodeLibelle={organismeList}
                  label="Organisme"
                  required={false}
                  setValues={setValues}
                  defaultValue={organismeList?.find(
                    (e: IdCodeLibelleType) => e.id === values.organismeId,
                  )}
                />
              </>
            ),
          },
        ]}
        handleShowClose={handleShowClose}
      />

      <TextInput
        required={false}
        name="queryTitle"
        label="Titre de la requête"
      />

      <TextAreaInput name="query" label="Requête SQL :" required={false} />
      <Col>
        <Alert
          variant="info"
          className="mt-2 mb-2 text-muted d-flex align-items-center"
        >
          <span>
            <IconInfo /> Tester la requête pour enregistrer les modifications
            sur le titre et/ou la requête.
          </span>
        </Alert>

        <Button
          type="button"
          variant="info"
          className="mt-3"
          onClick={() =>
            onTest({
              query: values.query,
              queryTitle: values.queryTitle,
            })
          }
        >
          Tester la requête
        </Button>
      </Col>
    </FormContainer>
  );
};

export default QueryForm;
